import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  getCountries,
  getCountryCallingCode,
  parsePhoneNumber,
} from "react-phone-number-input";
import "./PhoneInput.css";

const getFlagEmoji = (country) =>
  country
    .toUpperCase()
    .replace(/./g, (char) =>
      String.fromCodePoint(127397 + char.charCodeAt(0))
    );

const normalizeDigits = (value) => String(value || "").replace(/\D/g, "");

const debugPhoneInput = (stage, payload = {}) => {
  console.log(`[PhoneInput] ${stage}`, payload);
};

const splitPhoneValue = (value, fallbackCountry, countries = []) => {
  if (!value) {
    const emptyResult = { country: fallbackCountry, nationalNumber: "" };
    debugPhoneInput("split empty", { value, fallbackCountry, result: emptyResult });
    return emptyResult;
  }

  const digits = normalizeDigits(value);
  debugPhoneInput("split start", { value, fallbackCountry, digits });

  try {
    const parsed = parsePhoneNumber(value);
    if (parsed?.country && parsed.nationalNumber) {
      const parsedResult = {
        country: parsed.country,
        nationalNumber: parsed.nationalNumber || "",
      };
      debugPhoneInput("split parsed", {
        value,
        parsedCountry: parsed.country,
        parsedNationalNumber: parsed.nationalNumber,
        result: parsedResult,
      });
      return parsedResult;
    }
    debugPhoneInput("split parser incomplete", {
      value,
      parsedCountry: parsed?.country,
      parsedNationalNumber: parsed?.nationalNumber,
    });
  } catch {}

  const fallbackCallingCode = getCountryCallingCode(fallbackCountry);
  if (digits.startsWith(fallbackCallingCode)) {
    const fallbackResult = {
      country: fallbackCountry,
      nationalNumber: digits.slice(fallbackCallingCode.length),
    };
    debugPhoneInput("split fallback country", {
      value,
      fallbackCountry,
      fallbackCallingCode,
      result: fallbackResult,
    });
    return fallbackResult;
  }

  const matchingCountry = countries
    .map((country) => ({
      country,
      callingCode: getCountryCallingCode(country),
    }))
    .filter(({ callingCode }) => digits.startsWith(callingCode))
    .sort((a, b) => b.callingCode.length - a.callingCode.length)[0];

  if (matchingCountry) {
    const matchingResult = {
      country: matchingCountry.country,
      nationalNumber: digits.slice(matchingCountry.callingCode.length),
    };
    debugPhoneInput("split matched country", {
      value,
      matchingCountry,
      result: matchingResult,
    });
    return matchingResult;
  }

  const rawResult = {
    country: fallbackCountry,
    nationalNumber: digits,
  };
  debugPhoneInput("split raw fallback", { value, result: rawResult });
  return rawResult;
};

const PhoneInput = ({
  label = "phone",
  value,
  onChange,
  defaultCountry = "US",
  placeholder = "enter phone number",
  name = "phone",
  autoComplete = "tel",
  className = "",
  variant = "default",
}) => {
  const countries = useMemo(() => getCountries(), []);
  const [country, setCountry] = useState(defaultCountry);
  const [nationalNumber, setNationalNumber] = useState("");
  const lastEmittedValueRef = useRef(null);

  useEffect(() => {
    debugPhoneInput("effect value received", {
      value,
      lastEmittedValue: lastEmittedValueRef.current,
      country,
      nationalNumber,
    });

    if (value === lastEmittedValueRef.current) {
      debugPhoneInput("effect skipped own emission", {
        value,
        lastEmittedValue: lastEmittedValueRef.current,
      });
      return;
    }

    const next = splitPhoneValue(value, defaultCountry, countries);
    debugPhoneInput("effect hydrate external value", { value, next });
    setCountry(next.country);
    setNationalNumber(next.nationalNumber);
    lastEmittedValueRef.current = value || "";
  }, [value, defaultCountry, countries]);

  const emitChange = (nextCountry, nextNationalNumber) => {
    const digits = normalizeDigits(nextNationalNumber);
    const callingCode = getCountryCallingCode(nextCountry);
    const nextValue = digits ? `+${callingCode}${digits}` : "";
    debugPhoneInput("emit", {
      nextCountry,
      nextNationalNumber,
      digits,
      callingCode,
      nextValue,
    });
    lastEmittedValueRef.current = nextValue;
    onChange?.(nextValue);
  };

  const handleCountryChange = (event) => {
    const nextCountry = event.target.value;
    debugPhoneInput("country change", {
      previousCountry: country,
      nextCountry,
      nationalNumber,
    });
    setCountry(nextCountry);
    emitChange(nextCountry, nationalNumber);
  };

  const handleNumberChange = (event) => {
    debugPhoneInput("number input raw change", {
      rawValue: event.target.value,
      currentCountry: country,
      previousNationalNumber: nationalNumber,
    });
    const nextNationalNumber = normalizeDigits(event.target.value);
    debugPhoneInput("number input normalized", {
      nextNationalNumber,
      currentCountry: country,
    });
    setNationalNumber(nextNationalNumber);
    emitChange(country, nextNationalNumber);
  };

  return (
    <div className={`inyc-phone-field inyc-phone-field-${variant} ${className}`}>
      {label && <label className="inyc-label !mb-0">{label}</label>}
      <div className="inyc-phone-split-input">
        <select
          className="inyc-phone-country inyc-field-bare"
          value={country}
          onChange={handleCountryChange}
          aria-label="phone country"
        >
          {countries.map((item) => (
            <option key={item} value={item}>
              {getFlagEmoji(item)} +{getCountryCallingCode(item)}
            </option>
          ))}
        </select>
        <input
          className="inyc-phone-number inyc-field-bare"
          value={nationalNumber}
          onChange={handleNumberChange}
          placeholder={placeholder}
          name={name}
          type="tel"
          inputMode="tel"
          autoComplete={autoComplete}
        />
      </div>
    </div>
  );
};

export default PhoneInput;
